import {
  getBaseVariation,
  getAutonomousEndpointProductValue,
  isAemRemoteSupportDualProductVariation,
  isAutonomousProductSelection,
  AEM_AUTONOMOUS_PRODUCT_FORM_VALUE,
  CHECKBOX_PRODUCT_REMOTE_ACCESS,
  CHECKBOX_PRODUCT_REMOTE_SUPPORT
} from './trialFormBase';

export const isRetrialVariation = (variation) => variation.includes('retrial');

export const isSbaVariation = (variation) => variation.includes('sba');

export const isSosVariation = (variation) => variation.includes('sos');

export const isAemVariation = (variation) => variation.includes('aem');

export const isEnterpriseVariation = (variation) => variation.includes('enterprise');

/**
 * Email label is "Work Email" for Remote Support (standalone sos, or combined
 * picker with Remote Support selected), AEM, and Enterprise. Remote Access and
 * unset combined pickers keep "Email".
 */
export const usesWorkEmailLabel = (variation, isSba) => (
  isSba === false
  || isSosVariation(variation)
  || isAemVariation(variation)
  || isEnterpriseVariation(variation)
);

export const getLeadSource = (variation) => {
  const baseVariation = getBaseVariation(variation);
  if (isRetrialVariation(variation)) {
    if (isSbaVariation(variation)) return 'sba-retrial';
    if (isSosVariation(variation)) return 'sos-retrial';
    if (isAemVariation(variation)) return 'retrial-aem';
    return 'retrial';
  }
  if (isEnterpriseVariation(baseVariation)) return 'enterprise';
  if (baseVariation.includes('sos')) return 'sos';
  if (baseVariation.includes('aem')) return 'aem';
  if (baseVariation.includes('sba')) return 'sba-phone';
  return 'combined-top';
};

/**
 * Resolves `lead_source` for marketing API from URL variation + product selection.
 * - `/trial/aem-remote-support`: Remote Support → `sos`; else → `aem` (AEM product or unset).
 * - Combined / radio trials: when the autonomous/AEM product is selected → `aem`.
 * - Standalone `/trial/aem` (variation base `aem`): {@link getLeadSource} → `aem`.
 *
 * The AEM / autonomous-endpoint product option is exposed by default on combined-style
 * pickers (`/trial/choose`, `radio-combined-top`) whenever Contentful defines it; there is
 * no URL gate (`?aem=1`) anymore.
 */
export const resolveLeadSource = (variation, iWantToUseValue, inputs) => {
  if (isAemRemoteSupportDualProductVariation(variation)) {
    if (iWantToUseValue === 'Remote Support') return 'sos';
    return 'aem';
  }

  const autonomous = inputs && getAutonomousEndpointProductValue(inputs);
  if (autonomous && iWantToUseValue && isAutonomousProductSelection(iWantToUseValue, autonomous)) {
    return 'aem';
  }

  return getLeadSource(variation);
};

/**
 * `lead_source` for the checkbox combined picker, derived from the set of selected products.
 * Precedence (highest first): aem > remote-support > remote-access.
 *  - any selection including AEM/autonomous → `aem`
 *  - remote-support selected (no AEM)       → `sos`
 *  - only remote-access selected            → `combined-top` (backend maps to `sba`)
 *  - nothing selected                       → `combined-top` (fallback before validation)
 */
export const resolveCheckboxLeadSource = (selectedProducts = []) => {
  if (selectedProducts.includes(AEM_AUTONOMOUS_PRODUCT_FORM_VALUE)) return 'aem';
  if (selectedProducts.includes(CHECKBOX_PRODUCT_REMOTE_SUPPORT)) return 'sos';
  if (selectedProducts.includes(CHECKBOX_PRODUCT_REMOTE_ACCESS)) return 'combined-top';
  return 'combined-top';
};
